// Formats and limits for turning a quote from any system into an Omni intake (#2018, C1).

export type IntakeFormat = "pdf" | "image" | "spreadsheet" | "word" | "email" | "html" | "text";

export interface IntakeFileType {
  format: IntakeFormat;
  ext: string;
  aliases: readonly string[];
  mediaType: string;
  label: string;
}

export const INTAKE_FILE_TYPES: readonly IntakeFileType[] = [
  { format: "pdf", ext: "pdf", aliases: [], mediaType: "application/pdf", label: "PDF" },
  { format: "image", ext: "png", aliases: [], mediaType: "image/png", label: "PNG" },
  { format: "image", ext: "jpg", aliases: ["jpeg"], mediaType: "image/jpeg", label: "JPEG" },
  { format: "image", ext: "webp", aliases: [], mediaType: "image/webp", label: "WebP" },
  { format: "image", ext: "gif", aliases: [], mediaType: "image/gif", label: "GIF" },
  {
    format: "spreadsheet",
    ext: "xlsx",
    aliases: [],
    mediaType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    label: "Excel",
  },
  { format: "spreadsheet", ext: "xls", aliases: [], mediaType: "application/vnd.ms-excel", label: "Excel" },
  { format: "spreadsheet", ext: "csv", aliases: [], mediaType: "text/csv", label: "CSV" },
  {
    format: "word",
    ext: "docx",
    aliases: [],
    mediaType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    label: "Word",
  },
  { format: "word", ext: "doc", aliases: [], mediaType: "application/msword", label: "Word" },
  { format: "email", ext: "eml", aliases: [], mediaType: "message/rfc822", label: "Email" },
  { format: "html", ext: "html", aliases: ["htm"], mediaType: "text/html", label: "Web page" },
  { format: "text", ext: "txt", aliases: [], mediaType: "text/plain", label: "Text" },
];

const INTAKE_ACCEPT_PARTS: readonly string[] = [
  ...INTAKE_FILE_TYPES.flatMap((type) => [type.ext, ...type.aliases].map((ext) => `.${ext}`)),
  ...INTAKE_FILE_TYPES.map((type) => type.mediaType),
];

/** `<input accept>`: every extension, alias, and media type Omni reads. */
export const INTAKE_ACCEPT: string = INTAKE_ACCEPT_PARTS.join(",");

export const INTAKE_LIMITS = {
  fileBytes: 10485760,
  textChars: 200000,
  zipExpandedBytes: 52428800,
  zipEntries: 2000,
  sheets: 20,
  rowsPerSheet: 2000,
  pdfPages: 100,
  imageMegapixels: 40,
  imageBlockBytes: 3750000,
  imageBlockSide: 8000,
  emailAttachments: 10,
} as const;

/** Binary request body listing every intake media type. Shared by the v1 route table and OpenAPI. */
export function intakeUploadContent(): Record<string, { schema: { type: "string"; format: "binary" } }> {
  const content: Record<string, { schema: { type: "string"; format: "binary" } }> = {};
  for (const type of INTAKE_FILE_TYPES) {
    content[type.mediaType] = { schema: { type: "string", format: "binary" } };
  }
  return content;
}

/**
 * User-facing refusal strings (C7). Surfaces must use these verbatim.
 * KQM lookup strings live in kqm.ts.
 */
export const INTAKE_ERRORS = {
  fileTooLarge: "File exceeds 10MB limit.",
  unsupportedType:
    "Omni reads PDF, images (PNG, JPEG, WebP, GIF), Excel or CSV, Word, email (.eml), web pages and text files.",
  heic: "Omni can't read HEIC photos. Share it as JPEG or PNG.",
  outlookMsg: "Omni reads email saved as .eml. Save the message as .eml or PDF.",
  zipTooLarge: "This file expands to more than 50 MB. Save it as PDF.",
  imageMegapixels: "This image is over 40 megapixels.",
  imageBlock: "Save this image as PNG or JPEG.",
  textRequired: "Text is required.",
  textTooLong: "Text is over 200,000 characters.",
  textKind: "textKind must be document or transcript.",
  kqmStaffOnly: "Only Phoneware staff can import KQM quotes.",
  dealerRequired: "dealerId is required for Phoneware staff.",
  spreadsheetOpen: "Omni couldn't open this spreadsheet.",
  spreadsheetEmpty: "The spreadsheet has no rows to read.",
  wordOpen: "Omni couldn't open this Word file. Save it as PDF and try again.",
  emailEmpty: "The email has no text or attachment Omni can read.",
  tooManyPages: "This quote has more than 100 pages.",
} as const;

/**
 * RFC 4180 CSV. Records split on CRLF or LF. A quoted field may contain commas,
 * quotes (escaped by doubling), and line breaks. A trailing record separator does
 * not add an empty row.
 */
export function parseCsvRows(text: string): string[][] {
  if (text.length === 0) return [];
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const ch = text[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      pushField();
      i += 1;
      continue;
    }
    if (ch === "\r") {
      pushRow();
      i += text[i + 1] === "\n" ? 2 : 1;
      continue;
    }
    if (ch === "\n") {
      pushRow();
      i += 1;
      continue;
    }
    field += ch;
    i += 1;
  }

  if (inQuotes) {
    // An unclosed quote is still a field. Keep what was read.
    inQuotes = false;
  }
  if (field.length > 0 || row.length > 0) {
    pushRow();
  }
  return rows;
}
